import React, { useState, useEffect, useCallback, useRef } from 'react';
import debounce from 'lodash.debounce';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Grid, IconButton, InputAdornment, MenuItem, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography, Alert, alpha, Switch, FormControlLabel,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Add, Delete, Search, Refresh, CheckCircle, Cancel, Block, Edit,
  ListAlt, PersonAdd, Send, LocalShipping, PictureAsPdf, Assignment,
} from '@mui/icons-material';
import axios from 'axios';
import ClienteRapidoDialog from '../components/ClienteRapidoDialog';
import ProductoRapidoDialog from '../components/ProductoRapidoDialog';
import ProductoBuscadorDialog from '../components/ProductoBuscadorDialog';
import { obtenerEmpresaConfig } from '../utils/empresaConfig';
import { useResizableCols } from '../hooks/useResizableCols';

const ESTADO_COLOR = {
  BORRADOR: 'default', APROBADA: 'success', ENVIADA: 'info',
  RECIBIDA_PARCIAL: 'warning', RECIBIDA: 'success', ANULADA: 'error',
};

const COL_DEFAULTS_ORDEN_COMPRA = {
  producto:    220,
  descripcion: 220,
  cant:        80,
  umedida:     100,
  precio:      110,
  desc_pct:    70,
  igv:         70,
  subtotal:    100,
  total:       100,
  acciones:    45,
};

const ResizeHandleOrdenCompra = ({ onMouseDown }) => {
  const [hover, setHover] = useState(false);
  return (
    <span
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'absolute', right: 0, top: 0, bottom: 0, width: 8,
        cursor: 'col-resize', userSelect: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <span style={{
        width: hover ? 3 : 2, height: hover ? '70%' : '55%', borderRadius: 1,
        background: hover ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)',
        pointerEvents: 'none', transition: 'all 0.12s',
      }} />
    </span>
  );
};

const CONDICIONES_PAGO = ['CONTADO', 'CRÉDITO 15 DÍAS', 'CRÉDITO 30 DÍAS', 'CRÉDITO 60 DÍAS', 'CRÉDITO 90 DÍAS'];

const initialDetalle = {
  producto_id: '', producto_obj: null,
  descripcion_producto: '', unidad_medida: 'NIU',
  cantidad: 1, precio_unitario: 0, igv_porcentaje: 18,
  descuento_porcentaje: 0, descuento_monto: 0,
  subtotal: 0, igv_monto: 0, total: 0,
};

const initialForm = {
  fecha: new Date().toLocaleDateString('en-CA'),
  fecha_entrega: '',
  proveedor_id: '', proveedor_obj: null,
  condicion_pago: 'CONTADO',
  moneda: 'PEN', tipo_cambio: '',
  sucursal_id: '',
  observaciones: '',
  requerimiento_id: null,
  subtotal: 0, total_igv: 0, total: 0,
  detalles: [{ ...initialDetalle }],
};

function fmt(n) { return String(n || 0).padStart(5, '0'); }
function fmtNum(n, dec = 2) { return (parseFloat(n) || 0).toFixed(dec); }
function monedaSimbolo(m) { return m === 'USD' ? '$' : 'S/'; }

function calcDetalle(det, conIgv = false) {
  const cantidad  = parseFloat(det.cantidad)             || 0;
  const precio    = parseFloat(det.precio_unitario)      || 0;
  const descPct   = parseFloat(det.descuento_porcentaje) || 0;
  const igvPct    = parseFloat(det.igv_porcentaje)       || 0;
  const baseRow   = cantidad * precio;
  const descMonto = parseFloat((baseRow * descPct / 100).toFixed(2));
  let subtotal, igv_monto, total;
  if (conIgv && igvPct > 0) {
    total     = parseFloat((baseRow - descMonto).toFixed(2));
    subtotal  = parseFloat((total / (1 + igvPct / 100)).toFixed(2));
    igv_monto = parseFloat((total - subtotal).toFixed(2));
  } else {
    subtotal  = parseFloat((baseRow - descMonto).toFixed(2));
    igv_monto = parseFloat((subtotal * igvPct / 100).toFixed(2));
    total     = parseFloat((subtotal + igv_monto).toFixed(2));
  }
  return { ...det, descuento_monto: descMonto, subtotal, igv_monto, total };
}

function calcTotales(detalles) {
  const subtotal  = detalles.reduce((s, d) => s + (parseFloat(d.subtotal)  || 0), 0);
  const total_igv = detalles.reduce((s, d) => s + (parseFloat(d.igv_monto) || 0), 0);
  return {
    subtotal:  parseFloat(subtotal.toFixed(2)),
    total_igv: parseFloat(total_igv.toFixed(2)),
    total:     parseFloat((subtotal + total_igv).toFixed(2)),
  };
}

const SectionHeader = ({ children }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 0.5,
    borderLeft: '3px solid', borderColor: 'primary.main',
    pl: 1.5, py: 0.25, mb: 1.5,
    bgcolor: t => alpha(t.palette.primary.main, 0.06),
    borderRadius: '0 4px 4px 0',
  }}>
    <Typography variant="subtitle2" color="primary.dark" fontWeight={700}>{children}</Typography>
  </Box>
);

export default function OrdenCompraPage() {
  const { widths: colW, startResize } = useResizableCols('orden-compra-detail-cols', COL_DEFAULTS_ORDEN_COMPRA);
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');

  const [open, setOpen]     = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm]     = useState({ ...initialForm });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const [provInputValue, setProvInputValue] = useState('');
  const [proveedores, setProveedores]       = useState([]);
  const [openNuevoProv, setOpenNuevoProv]   = useState(false);

  const [sucursales, setSucursales]   = useState([]);
  const [unidades, setUnidades]       = useState([]);
  const [productOptions, setProductOptions]       = useState({});
  const [productInputValues, setProductInputValues] = useState({});
  const [preciosConIgv, setPreciosConIgv] = useState(false);

  const [openNuevoProducto, setOpenNuevoProducto] = useState(false);
  const [productoInputActivo, setProductoInputActivo] = useState({ idx: 0, texto: '' });
  const [openBuscadorProducto, setOpenBuscadorProducto] = useState(false);
  const [buscadorRowIdx, setBuscadorRowIdx] = useState(0);

  // Importar desde requerimiento
  const [openReqDialog, setOpenReqDialog] = useState(false);
  const [requerimientos, setRequerimientos] = useState([]);
  const [reqLoading, setReqLoading] = useState(false);

  // PDF dialog state
  const [openOcPdfDialog, setOpenOcPdfDialog] = useState(false);
  const [ocPdfTarget, setOcPdfTarget] = useState(null);
  const [ocPdfFormatoSeleccionado, setOcPdfFormatoSeleccionado] = useState(() => localStorage.getItem('orden_compra_pdf_formato_default') || 'A4');
  const [guardarOcPdfComoPredeterminado, setGuardarOcPdfComoPredeterminado] = useState(true);
  const [ocPdfResolucion, setOcPdfResolucion] = useState({});
  const [ocPdfResolucionCargando, setOcPdfResolucionCargando] = useState(false);

  const actualizarResolucionFormatoOc = async (nuevoFormato) => {
    if (!ocPdfTarget) return;
    setOcPdfResolucionCargando(true);
    try {
      const res = await axios.get(`/api/reporte-pdf/resuelve-orden-compra/${ocPdfTarget.id}`, { params: { formato: nuevoFormato } });
      setOcPdfResolucion(res.data || {});
    } catch (err) {
      console.error('Error resolving OC PDF template:', err);
    } finally {
      setOcPdfResolucionCargando(false);
    }
  };

  const abrirDialogoPdfOc = async (oc) => {
    const formatoGuardado = localStorage.getItem('orden_compra_pdf_formato_default') || 'A4';
    setOcPdfTarget(oc);
    setOcPdfFormatoSeleccionado(formatoGuardado);
    setGuardarOcPdfComoPredeterminado(true);
    setOcPdfResolucion({});
    setOpenOcPdfDialog(true);
    setOcPdfResolucionCargando(true);
    try {
      const res = await axios.get(`/api/reporte-pdf/resuelve-orden-compra/${oc.id}`, { params: { formato: formatoGuardado } });
      setOcPdfResolucion(res.data || {});
    } catch (err) {
      console.error('Error resolving OC PDF template:', err);
    } finally {
      setOcPdfResolucionCargando(false);
    }
  };

  const cerrarDialogoPdfOc = () => {
    setOpenOcPdfDialog(false);
    setOcPdfTarget(null);
  };

  const descargarPdfOc = () => {
    if (!ocPdfTarget) return;
    const formato = String(ocPdfFormatoSeleccionado || 'A4').toUpperCase();
    if (guardarOcPdfComoPredeterminado) {
      localStorage.setItem('orden_compra_pdf_formato_default', formato);
    }
    const tk = localStorage.getItem('token') || '';
    const query = new URLSearchParams({ formato, mode: 'inline', _ts: Date.now() }).toString();
    window.open(`/api/reporte-pdf/orden-compra/${ocPdfTarget.id}?${query}&token=${tk}`, '_blank');
    cerrarDialogoPdfOc();
  };

  const debouncedFetchRefs = useRef({});

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // ─ Catálogos ─────────────────────────────────────────────────────────────

  const fetchProveedores = useCallback(async () => {
    try {
      const r = await axios.get('/api/orden-compra/proveedores', { headers });
      setProveedores(r.data || []);
      return r.data || [];
    } catch { return []; }
  }, []);

  const fetchSucursales = useCallback(async () => {
    try {
      const r = await axios.get('/api/sucursales/mis-sucursales', { headers });
      const data = r.data || [];
      setSucursales(data);
      return data;
    } catch { return []; }
  }, []);

  const fetchUnidades = useCallback(async () => {
    try {
      const r = await axios.get('/api/unidades', { headers });
      setUnidades(r.data || []);
    } catch { setUnidades([]); }
  }, []);

  const fetchProductosForRow = useCallback((rowIdx, q) => {
    if (!debouncedFetchRefs.current[rowIdx]) {
      debouncedFetchRefs.current[rowIdx] = debounce(async (s) => {
        try {
          const res = await axios.get('/api/productos', { headers, params: { search: s, limit: 30 } });
          setProductOptions(prev => ({ ...prev, [rowIdx]: res.data?.data || res.data || [] }));
        } catch {
          setProductOptions(prev => ({ ...prev, [rowIdx]: [] }));
        }
      }, 300);
    }
    debouncedFetchRefs.current[rowIdx](q);
  }, []);

  const loadInitialProducts = useCallback(async (rowIdx) => {
    try {
      const res = await axios.get('/api/productos', { headers, params: { search: '', limit: 40 } });
      setProductOptions(prev => ({ ...prev, [rowIdx]: res.data?.data || res.data || [] }));
    } catch {}
  }, []);

  // ─ Listado ────────────────────────────────────────────────────────────────

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search };
      if (estadoFiltro) params.estado = estadoFiltro;
      const r = await axios.get('/api/orden-compra', { headers, params });
      setRows(r.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [search, estadoFiltro]);

  useEffect(() => { cargar(); }, [cargar]);

  // ─ Formulario ─────────────────────────────────────────────────────────────

  const handleFormChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleDetalleChange = (idx, field, value) => {
    setForm(prev => {
      const detalles = prev.detalles.map((d, i) => i !== idx ? d : calcDetalle({ ...d, [field]: value }, preciosConIgv));
      return { ...prev, detalles, ...calcTotales(detalles) };
    });
  };

  const handleProductoSelect = (idx, prod) => {
    if (!prod) return;
    setForm(prev => {
      const detalles = prev.detalles.map((d, i) => {
        if (i !== idx) return d;
        return calcDetalle({
          ...d,
          producto_id: prod.id,
          producto_obj: prod,
          descripcion_producto: prod.nombre || '',
          unidad_medida: prod.unidad_medida || 'NIU',
          precio_unitario: prod.precio_compra || prod.precio_costo || 0,
        }, preciosConIgv);
      });
      return { ...prev, detalles, ...calcTotales(detalles) };
    });
  };

  const handleBuscadorSelectOC = (prod) => {
    handleProductoSelect(buscadorRowIdx, prod);
    setProductInputValues(prev => ({ ...prev, [buscadorRowIdx]: prod.nombre || '' }));
  };

  const cargarRequerimientos = async () => {
    setReqLoading(true);
    try {
      const r = await axios.get('/api/orden-compra/requerimientos-aprobados', { headers });
      setRequerimientos(r.data || []);
    } catch { setRequerimientos([]); }
    setReqLoading(false);
  };

  const importarRequerimiento = (req) => {
    const newDetalles = req.detalles.map(d => calcDetalle({
      ...initialDetalle,
      producto_id: d.producto_id || '',
      producto_obj: d.producto_id ? { id: d.producto_id, nombre: d.producto_nombre, codigo: d.producto_codigo } : null,
      descripcion_producto: d.descripcion_producto || d.producto_nombre || '',
      unidad_medida: d.unidad_medida || 'NIU',
      cantidad: d.cantidad_solicitada || 1,
      requerimiento_detalle_id: d.id,
    }, preciosConIgv));
    const newVals = {};
    req.detalles.forEach((d, i) => { newVals[i] = d.producto_nombre || d.descripcion_producto || ''; });
    setProductInputValues(newVals);
    setForm(prev => ({
      ...prev,
      requerimiento_id: req.id,
      detalles: newDetalles,
      ...calcTotales(newDetalles),
    }));
    setOpenReqDialog(false);
  };

  const handleAddDetalle = () => {
    setForm(prev => {
      const newIdx = prev.detalles.length;
      loadInitialProducts(newIdx);
      return { ...prev, detalles: [...prev.detalles, { ...initialDetalle }] };
    });
  };

  const handleRemoveDetalle = (idx) => {
    setForm(prev => {
      const detalles = prev.detalles.length > 1
        ? prev.detalles.filter((_, i) => i !== idx)
        : prev.detalles;
      return { ...prev, detalles, ...calcTotales(detalles) };
    });
  };

  // ─ Abrir formulario ───────────────────────────────────────────────────────

  const abrirNuevo = async () => {
    setEditId(null);
    setError('');
    setProvInputValue('');
    debouncedFetchRefs.current = {};
    setProductOptions({});
    setProductInputValues({});
    const [sucs] = await Promise.all([fetchSucursales(), fetchUnidades(), fetchProveedores()]);
    const autoSucursal = sucs.length === 1 ? sucs[0].id : '';
    setForm({ ...initialForm, sucursal_id: autoSucursal, detalles: [{ ...initialDetalle }] });
    obtenerEmpresaConfig().then(cfg => setPreciosConIgv(Boolean(cfg?.precios_incluyen_igv))).catch(() => {});
    loadInitialProducts(0);
    setOpen(true);
  };

  const abrirEditar = async (id) => {
    try {
      const [rData, sucs] = await Promise.all([
        axios.get(`/api/orden-compra/${id}`, { headers }),
        fetchSucursales(),
      ]);
      await Promise.all([fetchUnidades(), fetchProveedores()]);
      const d = rData.data;
      debouncedFetchRefs.current = {};
      setProductOptions({});

      const detalles = d.detalles?.length
        ? d.detalles.map(det => ({
            producto_id: det.producto_id || '',
            producto_obj: det.producto_id ? { id: det.producto_id, nombre: det.producto_nombre || det.descripcion_producto, codigo: det.producto_codigo } : null,
            descripcion_producto: det.descripcion_producto || '',
            unidad_medida: det.unidad_medida || 'NIU',
            cantidad: det.cantidad || 1,
            precio_unitario: det.precio_unitario || 0,
            igv_porcentaje: det.igv_porcentaje !== undefined ? det.igv_porcentaje : 18,
            descuento_porcentaje: det.descuento_porcentaje || 0,
            descuento_monto: det.descuento_monto || 0,
            subtotal: det.subtotal || 0,
            igv_monto: det.igv_monto || 0,
            total: det.total || 0,
          }))
        : [{ ...initialDetalle }];

      const initVals = {};
      detalles.forEach((det, i) => { initVals[i] = det.producto_obj?.nombre || det.descripcion_producto || ''; });
      setProductInputValues(initVals);
      detalles.forEach((_, i) => loadInitialProducts(i));

      const provObj = { id: d.proveedor_id, razon_social: d.proveedor_nombre, numero_documento: d.proveedor_ruc };
      setProvInputValue(d.proveedor_id ? `${d.proveedor_ruc || ''} - ${d.proveedor_nombre || ''}` : '');

      setForm({
        fecha: d.fecha?.slice(0, 10) || '',
        fecha_entrega: d.fecha_entrega?.slice(0, 10) || '',
        proveedor_id: d.proveedor_id || '',
        proveedor_obj: d.proveedor_id ? provObj : null,
        condicion_pago: d.condicion_pago || 'CONTADO',
        moneda: d.moneda || 'PEN',
        tipo_cambio: d.tipo_cambio || '',
        sucursal_id: d.sucursal_id || (sucs.length === 1 ? sucs[0].id : ''),
        observaciones: d.observaciones || '',
        subtotal: d.subtotal || 0,
        total_igv: d.total_igv || 0,
        total: d.total || 0,
        detalles,
      });
      setEditId(id);
      setOpen(true);
    } catch { /* */ }
  };

  // ─ Guardar ────────────────────────────────────────────────────────────────

  const guardar = async () => {
    setError('');
    if (!form.fecha)         { setError('Ingrese la fecha'); return; }
    if (!form.proveedor_id)  { setError('Seleccione un proveedor'); return; }
    const detalles = form.detalles.filter(d => d.descripcion_producto || d.producto_id);
    if (!detalles.length)    { setError('Agregue al menos un producto'); return; }
    setSaving(true);
    try {
      const payload = { ...form, detalles };
      if (editId) {
        await axios.put(`/api/orden-compra/${editId}`, payload, { headers });
      } else {
        await axios.post('/api/orden-compra', payload, { headers });
      }
      setOpen(false);
      cargar();
    } catch (e) {
      setError(e?.response?.data?.error || 'Error al guardar');
    }
    setSaving(false);
  };

  // ─ Acciones ───────────────────────────────────────────────────────────────

  const cambiarEstado = async (id, estado) => {
    const labels = { APROBADA: 'aprobar', ENVIADA: 'marcar como enviada', RECIBIDA: 'marcar como recibida' };
    if (!window.confirm(`¿${labels[estado] || estado} esta orden?`)) return;
    try { await axios.put(`/api/orden-compra/${id}/estado/${estado}`, {}, { headers }); cargar(); } catch { /* */ }
  };

  const anular = async (id) => {
    if (!window.confirm('¿Anular esta orden de compra?')) return;
    try { await axios.put(`/api/orden-compra/${id}/anular`, {}, { headers }); cargar(); } catch { /* */ }
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 2 }}>
      {/* Cabecera página */}
      <Paper elevation={0} sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 1.5, gap: 1, bgcolor: t => alpha(t.palette.primary.main, 0.06), borderRadius: 2 }}>
        <ListAlt color="primary" sx={{ fontSize: 28 }} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" fontWeight={700} color="primary.dark">Órdenes de Compra</Typography>
          <Typography variant="caption" color="text.secondary">Gestión de órdenes a proveedores</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={abrirNuevo}>Nueva OC</Button>
      </Paper>

      {/* Búsqueda */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small" placeholder="Buscar proveedor, nro..."
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ flex: '1 1 220px' }}
        />
        <TextField select label="Estado" size="small" sx={{ minWidth: 150 }}
          value={estadoFiltro} onChange={e => setEstadoFiltro(e.target.value)}>
          <MenuItem value="">Todos</MenuItem>
          {['BORRADOR', 'APROBADA', 'ENVIADA', 'RECIBIDA_PARCIAL', 'RECIBIDA', 'ANULADA'].map(e => (
            <MenuItem key={e} value={e}>{e}</MenuItem>
          ))}
        </TextField>
        <IconButton onClick={cargar} disabled={loading} color="primary"><Refresh /></IconButton>
      </Paper>

      {/* Tabla listado */}
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              {['Nro.', 'Fecha', 'Proveedor', 'F. Entrega', 'Moneda', 'Total', 'Estado', 'Acciones'].map(h => (
                <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>Cargando...</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay órdenes de compra registradas</TableCell></TableRow>
            ) : rows.map((row, i) => (
              <TableRow key={row.id} hover sx={{ bgcolor: i % 2 === 0 ? 'grey.50' : 'white' }}>
                <TableCell sx={{ fontWeight: 600 }}>OC-{fmt(row.numero)}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.fecha?.slice(0, 10)}</TableCell>
                <TableCell>{row.proveedor_nombre || '—'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.fecha_entrega?.slice(0, 10) || '—'}</TableCell>
                <TableCell>{row.moneda}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'success.dark' }}>
                  {monedaSimbolo(row.moneda)} {fmtNum(row.total)}
                </TableCell>
                <TableCell>
                  <Chip label={row.estado} color={ESTADO_COLOR[row.estado] || 'default'} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {row.estado === 'BORRADOR' && (
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => abrirEditar(row.id)}><Edit fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    {row.estado === 'BORRADOR' && (
                      <Tooltip title="Aprobar">
                        <IconButton size="small" color="success" onClick={() => cambiarEstado(row.id, 'APROBADA')}><CheckCircle fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    {row.estado === 'APROBADA' && (
                      <Tooltip title="Marcar Enviada">
                        <IconButton size="small" color="info" onClick={() => cambiarEstado(row.id, 'ENVIADA')}><Send fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    {row.estado === 'ENVIADA' && (
                      <Tooltip title="Marcar Recibida">
                        <IconButton size="small" color="success" onClick={() => cambiarEstado(row.id, 'RECIBIDA')}><LocalShipping fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    {!['ANULADA', 'RECIBIDA'].includes(row.estado) && (
                      <Tooltip title="Anular">
                        <IconButton size="small" color="warning" onClick={() => anular(row.id)}><Block fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="PDF">
                      <IconButton size="small" color="secondary" onClick={() => abrirDialogoPdfOc(row)}><PictureAsPdf fontSize="small" /></IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ─── Diálogo crear/editar ─────────────────────────────────────────── */}
      <Dialog open={open} onClose={(_, r) => { if (r !== 'backdropClick') setOpen(false); }}
        maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ListAlt />
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {editId ? `Editar OC-${fmt(editId)}` : 'Nueva Orden de Compra'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>Complete los datos de la orden al proveedor</Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* Sección: Proveedor */}
          <SectionHeader>Proveedor y Condiciones</SectionHeader>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item sx={{ minWidth: '30em' }}>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Autocomplete
                  sx={{ flex: 1 }}
                  options={proveedores}
                  getOptionLabel={o => `${o.numero_documento || ''} - ${o.razon_social || ''}`}
                  filterOptions={(options, { inputValue }) => {
                    const q = inputValue.toLowerCase();
                    if (!q) return options;
                    return options.filter(o =>
                      (o.numero_documento || '').includes(q) ||
                      (o.razon_social || '').toLowerCase().includes(q)
                    );
                  }}
                  value={form.proveedor_obj}
                  inputValue={provInputValue}
                  onOpen={() => { if (proveedores.length === 0) fetchProveedores(); }}
                  onInputChange={(_, v) => setProvInputValue(v)}
                  onChange={(_, v) => {
                    setProvInputValue(v ? `${v.numero_documento || ''} - ${v.razon_social || ''}` : '');
                    handleFormChange('proveedor_id', v?.id || '');
                    handleFormChange('proveedor_obj', v);
                  }}
                  renderInput={p => <TextField {...p} label="Proveedor *" size="small" fullWidth />}
                  noOptionsText={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>No encontrado</span>
                      <Button size="small" startIcon={<PersonAdd />} onClick={() => setOpenNuevoProv(true)}>
                        Registrar proveedor
                      </Button>
                    </Box>
                  }
                  isOptionEqualToValue={(o, v) => o.id === v?.id}
                />
                <Tooltip title="Registrar nuevo proveedor">
                  <IconButton size="small" color="primary" onClick={() => setOpenNuevoProv(true)}>
                    <PersonAdd fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item sx={{ minWidth: '11em' }}>
              <TextField label="Fecha *" type="date" size="small" fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.fecha}
                onChange={e => handleFormChange('fecha', e.target.value)} />
            </Grid>
            <Grid item sx={{ minWidth: '11em' }}>
              <TextField label="Fecha Entrega" type="date" size="small" fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.fecha_entrega}
                onChange={e => handleFormChange('fecha_entrega', e.target.value)} />
            </Grid>
            <Grid item sx={{ minWidth: '20em' }}>
              <TextField select label="Condición de Pago" size="small" fullWidth
                value={form.condicion_pago}
                onChange={e => handleFormChange('condicion_pago', e.target.value)}>
                {CONDICIONES_PAGO.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item sx={{ minWidth: '10em' }}>
              <TextField select label="Moneda" size="small" fullWidth
                value={form.moneda}
                onChange={e => handleFormChange('moneda', e.target.value)}>
                <MenuItem value="PEN">PEN (S/)</MenuItem>
                <MenuItem value="USD">USD ($)</MenuItem>
              </TextField>
            </Grid>
            {form.moneda !== 'PEN' && (
              <Grid item sx={{ minWidth: '10em' }}>
                <TextField label="T. Cambio" type="number" size="small" fullWidth
                  value={form.tipo_cambio}
                  onChange={e => handleFormChange('tipo_cambio', e.target.value)} />
              </Grid>
            )}
            <Grid item sx={{ minWidth: '16em' }}>
              <TextField select label="Sucursal" size="small" fullWidth
                value={form.sucursal_id}
                onChange={e => handleFormChange('sucursal_id', e.target.value)}>
                <MenuItem value="">Sin sucursal</MenuItem>
                {sucursales.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.nombre || `Sucursal ${s.id}`}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item sx={{ minWidth: '28em' }}>
              <TextField label="Observaciones" size="small" fullWidth
                value={form.observaciones}
                onChange={e => handleFormChange('observaciones', e.target.value)} />
            </Grid>
          </Grid>

          {/* Sección: Detalle de productos */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              borderLeft: '3px solid', borderColor: 'primary.main',
              pl: 1.5, py: 0.25, bgcolor: t => alpha(t.palette.primary.main, 0.06),
              borderRadius: '0 4px 4px 0', flexGrow: 1,
            }}>
              <Typography variant="subtitle2" color="primary.dark" fontWeight={700}>Detalle de Productos</Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch size="small" checked={preciosConIgv}
                  onChange={e => {
                    const v = e.target.checked;
                    setPreciosConIgv(v);
                    setForm(prev => {
                      const detalles = prev.detalles.map(d => calcDetalle(d, v));
                      return { ...prev, detalles, ...calcTotales(detalles) };
                    });
                  }}
                />
              }
              label={<Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>Precios c/ IGV</Typography>}
              sx={{ mx: 1, mb: 0 }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="outlined" color="secondary" startIcon={<Assignment />}
                onClick={() => { cargarRequerimientos(); setOpenReqDialog(true); }}
                sx={{ whiteSpace: 'nowrap' }}>
                Importar Req.
              </Button>
              <Button size="small" variant="outlined" startIcon={<Add />} onClick={handleAddDetalle} sx={{ whiteSpace: 'nowrap' }}>
                Agregar producto
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper} elevation={0} variant="outlined"
            sx={{ mb: 2, overflowX: 'auto', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: colW.producto }} />
                <col style={{ width: colW.descripcion }} />
                <col style={{ width: colW.cant }} />
                <col style={{ width: colW.umedida }} />
                <col style={{ width: colW.precio }} />
                <col style={{ width: colW.desc_pct }} />
                <col style={{ width: colW.igv }} />
                <col style={{ width: colW.subtotal }} />
                <col style={{ width: colW.total }} />
                <col style={{ width: colW.acciones }} />
              </colgroup>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  {[
                    { key: 'producto',    label: 'Producto' },
                    { key: 'descripcion', label: 'Descripción' },
                    { key: 'cant',        label: 'Cant.', align: 'center' },
                    { key: 'umedida',     label: 'U.M.', align: 'center' },
                    { key: 'precio',      label: 'Precio Unit.', align: 'right' },
                    { key: 'desc_pct',    label: 'Desc.%', align: 'center' },
                    { key: 'igv',         label: 'IGV %', align: 'center' },
                    { key: 'subtotal',    label: 'Subtotal', align: 'right' },
                    { key: 'total',       label: 'Total', align: 'right' },
                    { key: 'acciones',    label: '', align: 'center' },
                  ].map(col => (
                    <TableCell key={col.key} align={col.align} sx={{
                      position: 'relative', color: 'white', fontWeight: 'bold',
                      overflow: 'hidden', whiteSpace: 'nowrap', userSelect: 'none',
                    }}>
                      {col.label}
                      {col.key !== 'acciones' && (
                        <ResizeHandleOrdenCompra onMouseDown={e => startResize(col.key, e)} />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {form.detalles.map((det, idx) => (
                  <TableRow key={idx} sx={{ '&:hover': { bgcolor: 'action.hover' }, bgcolor: idx % 2 === 0 ? 'grey.50' : 'white' }}>
                    <TableCell sx={{ overflow: 'hidden' }}>
                      <Tooltip title={det.producto_obj ? `${det.producto_obj.codigo ? det.producto_obj.codigo + ' - ' : ''}${det.producto_obj.nombre || ''}` : ''}>
                        <Autocomplete
                          size="small"
                          options={productOptions[idx] || []}
                          filterOptions={x => x}
                          getOptionLabel={o => `${o.codigo ? o.codigo + ' - ' : ''}${o.nombre || ''}`}
                          value={det.producto_obj || null}
                          inputValue={productInputValues[idx] || ''}
                          onOpen={() => { if (!productOptions[idx]?.length) loadInitialProducts(idx); }}
                          onInputChange={(_, v) => {
                            setProductInputValues(prev => ({ ...prev, [idx]: v }));
                            setProductoInputActivo({ idx, texto: v });
                            if (v.length === 0 || v.length >= 2) fetchProductosForRow(idx, v);
                          }}
                          onChange={(_, v) => handleProductoSelect(idx, v)}
                          renderInput={p => <TextField {...p} placeholder="Buscar producto..." />}
                          isOptionEqualToValue={(o, v) => o.id === v?.id}
                          noOptionsText={
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span>No encontrado</span>
                              <Button size="small" startIcon={<Add />} onClick={() => { setProductoInputActivo({ idx, texto: productInputValues[idx] || '' }); setOpenNuevoProducto(true); }}>
                                Nuevo producto
                              </Button>
                            </Box>
                          }
                          sx={{ minWidth: 0, width: '100%', '& .MuiInputBase-input': { textOverflow: 'ellipsis' } }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ overflow: 'hidden' }}>
                      <Tooltip title={det.descripcion_producto || ''}>
                        <TextField size="small" fullWidth value={det.descripcion_producto}
                          onChange={e => handleDetalleChange(idx, 'descripcion_producto', e.target.value)}
                          onKeyDown={e => { if (e.key === 'F1') { e.preventDefault(); setBuscadorRowIdx(idx); setOpenBuscadorProducto(true); } }}
                          sx={{ '& .MuiInputBase-input': { textOverflow: 'ellipsis' } }}
                          InputProps={{
                            endAdornment: (
                              <Tooltip title="Buscar producto (F1)">
                                <IconButton size="small" edge="end"
                                  onClick={() => { setBuscadorRowIdx(idx); setOpenBuscadorProducto(true); }}>
                                  <Search fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ),
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="number" value={det.cantidad}
                        onChange={e => handleDetalleChange(idx, 'cantidad', e.target.value)}
                        inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', width: 65 } }} />
                    </TableCell>
                    <TableCell>
                      <Autocomplete
                        size="small"
                        options={unidades}
                        getOptionLabel={u => u.abreviatura || ''}
                        value={unidades.find(u => u.abreviatura === det.unidad_medida) || null}
                        onChange={(_, v) => handleDetalleChange(idx, 'unidad_medida', v?.abreviatura || '')}
                        renderInput={p => <TextField {...p} placeholder="U.M." />}
                        isOptionEqualToValue={(o, v) => o.abreviatura === v?.abreviatura}
                        sx={{ minWidth: 85 }}
                        disableClearable
                      />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="number" value={det.precio_unitario}
                        onChange={e => handleDetalleChange(idx, 'precio_unitario', e.target.value)}
                        inputProps={{ min: 0, step: 0.0001, style: { textAlign: 'right', width: 90 } }} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="number" value={det.descuento_porcentaje}
                        onChange={e => handleDetalleChange(idx, 'descuento_porcentaje', e.target.value)}
                        inputProps={{ min: 0, max: 100, step: 0.01, style: { textAlign: 'center', width: 55 } }} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="number" value={det.igv_porcentaje}
                        onChange={e => handleDetalleChange(idx, 'igv_porcentaje', e.target.value)}
                        inputProps={{ min: 0, max: 100, step: 1, style: { textAlign: 'center', width: 55 } }} />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {fmtNum(det.subtotal)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold" color="primary.dark">
                        {fmtNum(det.total)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="error" onClick={() => handleRemoveDetalle(idx)}
                        disabled={form.detalles.length === 1}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Totales */}
          <Grid container justifyContent="flex-end">
            <Grid item xs={12} sm={6} md={4}>
              <Paper elevation={2} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'primary.light' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="body2" color="text.secondary">Subtotal (sin IGV):</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {monedaSimbolo(form.moneda)} {fmtNum(form.subtotal)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">IGV:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {monedaSimbolo(form.moneda)} {fmtNum(form.total_igv)}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, alignItems: 'center' }}>
                  <Typography variant="subtitle1" fontWeight="bold">TOTAL:</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary.main">
                    {monedaSimbolo(form.moneda)} {fmtNum(form.total)}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1, bgcolor: 'grey.50' }}>
          <Button onClick={() => setOpen(false)} startIcon={<Cancel />} color="inherit">Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={saving} startIcon={<ListAlt />} size="large">
            {saving ? 'Guardando...' : (editId ? 'Actualizar OC' : 'Guardar Orden de Compra')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Dialog Importar Requerimiento ──────────────────────────────────── */}
      <Dialog open={openReqDialog} onClose={() => setOpenReqDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'secondary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Assignment />
          Importar desde Requerimiento Aprobado
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {reqLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          ) : requerimientos.length === 0 ? (
            <Alert severity="info">No hay requerimientos aprobados disponibles.</Alert>
          ) : (
            <TableContainer sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'secondary.main' }}>
                    {['Nro.', 'Fecha', 'Área', 'Urgencia', 'Ítems', ''].map(h => (
                      <TableCell key={h} sx={{ color: 'white', fontWeight: 700 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requerimientos.map(r => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>REQ-{String(r.numero || '').padStart(5, '0')}</TableCell>
                      <TableCell>{r.fecha?.slice(0, 10)}</TableCell>
                      <TableCell>{r.area_solicitante || '—'}</TableCell>
                      <TableCell>{r.urgencia || '—'}</TableCell>
                      <TableCell>{r.detalles?.length || 0} producto(s)</TableCell>
                      <TableCell>
                        <Button size="small" variant="contained" color="secondary"
                          onClick={() => importarRequerimiento(r)}>
                          Importar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReqDialog(false)} variant="outlined">Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* PDF Format Dialog - Orden de Compra */}
      <Dialog open={openOcPdfDialog} onClose={cerrarDialogoPdfOc} maxWidth="sm" fullWidth>
        <DialogTitle>Descargar PDF - Orden de Compra</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            Selecciona el formato de impresión. El formato elegido puede quedar como predeterminado.
          </Alert>
          {ocPdfResolucionCargando ? (
            <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">Resolviendo plantilla...</Typography>
            </Box>
          ) : ocPdfResolucion && ocPdfResolucion.plantilla_nombre ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2"><strong>Plantilla:</strong> {ocPdfResolucion.plantilla_nombre}</Typography>
              <Typography variant="body2"><strong>Ámbito:</strong> {ocPdfResolucion.scope === 'Global' ? 'Global (todas las sucursales)' : ocPdfResolucion.scope === 'Specific' ? 'Específica para esta sucursal' : ocPdfResolucion.scope}</Typography>
              {ocPdfResolucion.is_inherited && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#1976d2' }}>
                  Esta sucursal hereda la asignación de la configuración global
                </Typography>
              )}
            </Alert>
          ) : null}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5, mb: 2 }}>
            {[
              { codigo: 'A4', titulo: 'A4', detalle: 'Formato completo' },
              { codigo: 'A5', titulo: 'A5', detalle: 'Formato compacto' },
              { codigo: 'T80', titulo: 'Ticket 80mm', detalle: 'Impresora térmica' }
            ].map((item) => (
              <Paper
                key={item.codigo}
                onClick={() => {
                  setOcPdfFormatoSeleccionado(item.codigo);
                  actualizarResolucionFormatoOc(item.codigo);
                }}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: '2px solid',
                  borderColor: ocPdfFormatoSeleccionado === item.codigo ? '#1976d2' : '#d0d7de',
                  backgroundColor: ocPdfFormatoSeleccionado === item.codigo ? '#e3f2fd' : '#fff'
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>{item.titulo}</Typography>
                <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>{item.detalle}</Typography>
                {localStorage.getItem('orden_compra_pdf_formato_default') === item.codigo && (
                  <Chip label="Predeterminado" color="primary" size="small" sx={{ mt: 1, display: 'flex', mx: 'auto', width: 'fit-content' }} />
                )}
              </Paper>
            ))}
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={guardarOcPdfComoPredeterminado}
                onChange={(e) => setGuardarOcPdfComoPredeterminado(e.target.checked)}
                color="primary"
              />
            }
            label={`Guardar ${ocPdfFormatoSeleccionado} como formato predeterminado`}
          />
          {ocPdfTarget && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Documento: OC-{String(ocPdfTarget.numero || ocPdfTarget.id || '').padStart(5, '0')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarDialogoPdfOc}>Cancelar</Button>
          <Button onClick={descargarPdfOc} variant="contained">
            Descargar {ocPdfFormatoSeleccionado}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ClienteRapidoDialog para proveedor */}
      <ClienteRapidoDialog
        open={openNuevoProv}
        onClose={() => setOpenNuevoProv(false)}
        tipo="PROVEEDOR"
        onClienteCreado={(prov) => {
          setProveedores(prev => [prov, ...prev]);
          setProvInputValue(`${prov.numero_documento || ''} - ${prov.razon_social || ''}`);
          handleFormChange('proveedor_id', prov.id);
          handleFormChange('proveedor_obj', prov);
          setOpenNuevoProv(false);
        }}
      />

      {/* ProductoRapidoDialog */}
      <ProductoRapidoDialog
        open={openNuevoProducto}
        onClose={() => setOpenNuevoProducto(false)}
        initialNombre={productoInputActivo.texto}
        onProductoCreado={(prod) => {
          const idx = productoInputActivo.idx;
          setProductInputValues(prev => ({ ...prev, [idx]: prod.nombre || '' }));
          setProductOptions(prev => ({ ...prev, [idx]: [prod, ...(prev[idx] || [])] }));
          handleProductoSelect(idx, prod);
          setOpenNuevoProducto(false);
        }}
      />
      <ProductoBuscadorDialog
        open={openBuscadorProducto}
        onClose={() => setOpenBuscadorProducto(false)}
        onSelect={handleBuscadorSelectOC}
      />
    </Box>
  );
}
